import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  ConfirmationToken,
  Stripe,
  StripeElements,
  loadStripe,
} from "@stripe/stripe-js"
import {
  Config,
  CreateDonationPayload,
  DateStampString,
  DraftFrequency,
  StripeCustomerSessionClientSecret,
  StripePaymentMethodDataWithMetadata,
} from "./types"
import { frequencyToSchedule } from "./utils/frequency"
import { RetryableDonationFormInvariantError } from "./errors"

export async function fetchStartDates(
  frequency: DraftFrequency,
): Promise<DateStampString[]> {
  const schedule = frequencyToSchedule(frequency)
  const response = await sessionApiClient.post(
    "/giving/v2/recurring_donation_start_dates",
    { data: { attributes: { schedule } } },
  )
  return response.data.attributes.occurrences
}

// CSC: Customer Session Client
async function fetchStripeCSCSecret(): Promise<StripeCustomerSessionClientSecret> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Fetching stripe csc secret")
  const response = await sessionApiClient.post(
    "/giving/v2/me/stripe_customer_session",
  )
  return response.data.attributes.client_secret
}
export function fetchStripeCSCSecretQuery() {
  return {
    queryKey: ["DonationForm", "stripeCustomerSessionClientSecret"],
    queryFn: fetchStripeCSCSecret,
    staleTime: Infinity,
  }
}

async function fetchStripeObject(config: Config) {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Fetching stripe object")
  return await loadStripe(config.stripe_publishable_key, {
    stripeAccount: config.stripe_account_identifier,
  })
}
export function fetchStripeObjectQuery(config: Config) {
  return {
    queryKey: [
      "DonationForm",
      "stripeObject",
      config.stripe_publishable_key,
      config.stripe_account_identifier,
    ],
    queryFn: () => fetchStripeObject(config),
    staleTime: Infinity,
  }
}

export async function createStripeConfirmationToken({
  stripe,
  stripeElements,
  billingName,
  billingEmail,
  organizationId,
}: {
  stripe: Stripe
  stripeElements: StripeElements
  billingName: string
  billingEmail: string
  organizationId: number
}): Promise<ConfirmationToken> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Creating confirmation token")
  await stripeElements.submit()
  const confirmationTokenResult = await stripe.createConfirmationToken({
    elements: stripeElements,
    params: {
      payment_method_data: {
        billing_details: {
          name: billingName,
          email: billingEmail,
        },
        allow_redisplay: "always",
        metadata: {
          planning_center_source_app: "Giving",
          planning_center_organization_id: organizationId,
        },
      } as StripePaymentMethodDataWithMetadata,
      // TODO: Get this from donation form config (church_center_stripe_setup_intent_url in Giving)
      return_url: "http://giving.pco.test",
    },
  })
  if (confirmationTokenResult.confirmationToken) {
    return confirmationTokenResult.confirmationToken
  } else {
    throw new RetryableDonationFormInvariantError()
  }
}

export async function createDonation({
  payload,
}: {
  payload: CreateDonationPayload
}) {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Creating donation")
  await sessionApiClient.post("/giving/v2/create_donation", payload)
}
